import React, { useEffect, useRef, useState } from 'react';

export default function BudgetStylePicker({
  open,
  onOpen,
  displayValue,
  placeholder,
  searchPlaceholder,
  options,
  onSelect,
  description,
  layout = 'stacked',
  inputClassName = 'budget-lines-input',
  menuClassName = 'w-[500px]',
}) {
  const [query, setQuery] = useState('');
  const anchorRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = menuClassName.includes('w-full') ? rect.width : Math.max(rect.width, 500);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 280 && rect.top > spaceBelow;
      setMenuStyle(openUp
        ? { position: 'fixed', left, width, bottom: window.innerHeight - rect.top + 2, zIndex: 9999 }
        : { position: 'fixed', left, width, top: rect.bottom + 2, zIndex: 9999 });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, menuClassName]);

  const term = query.trim().toLowerCase();
  const visible = !term
    ? options
    : options.filter((option) => `${option.primary} ${option.secondary || ''}`.toLowerCase().includes(term));

  return (
    <div>
      <div className="dropdown-container relative" ref={anchorRef}>
        <input
          type="text"
          readOnly
          value={displayValue}
          placeholder={placeholder}
          className={inputClassName}
          onFocus={onOpen}
        />
        {open && menuStyle && (
          <div
            className={`${menuClassName} bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto`}
            style={menuStyle}
          >
            <input
              type="text"
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={query}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
            />
            {visible.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
            ) : visible.map((option) => (
              <div
                key={option.vuid || option.primary}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onSelect(option.vuid)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                {layout === 'inline' ? (
                  <>
                    <span className="font-medium text-gray-900">{option.primary}</span>
                    {option.secondary ? <span className="ml-2 text-sm text-gray-600">{option.secondary}</span> : null}
                  </>
                ) : (
                  <>
                    <div className="font-medium text-gray-900">{option.primary}</div>
                    {option.secondary ? <div className="text-sm text-gray-600">{option.secondary}</div> : null}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {description ? (
        <div className="text-xs text-gray-600 mt-1 leading-snug">{description}</div>
      ) : null}
    </div>
  );
}
